import React, { ReactNode, useLayoutEffect, useRef } from 'react';
import {
  Table,
  Input,
  Button,
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit';
import SearchIcon from 'assets/search.svg?react';
import PlusIcon from 'assets/plus.svg?react';
import FilterIcon from 'assets/filter_outlined.svg?react';
import LoadingSpinner from 'components/LoadingSpinner';
import BlueBadge from 'components/BlueBadge';
import { PAGINATION_HEIGHT } from 'screens/Applications&WorkOrders/Components/Constants';
import 'screens/Applications&WorkOrders/Components/ListView/ListView.css';

interface ListViewProps {
  // Header
  icon: ReactNode;
  title: string;

  // Search
  searchPlaceholder?: string;
  searchText: string;
  onSearchChange: (value: string) => void;
  sortDropdown?: ReactNode;

  // Actions
  createButtonLabel: string;
  onCreateClick: () => void;
  filterButtonLabel: string;
  onFilterClick: () => void;
  hasActiveFilters?: boolean;

  // Table
  dataSource: any[];
  columns: any[];
  rowKey: string;
  rowSelection?: any;
  pagination?: any;
  onTableChange?: () => void;
  onRowClick?: (record: any) => void;
  isLoading?: boolean;

  // Empty state
  emptyMessage?: string;

  // Additional panels
  detailPanel?: ReactNode;
  filterDrawer?: ReactNode;

  // CSS class
  className?: string;
}

const ICON_CLASS = 'w-4 h-4';
const BUTTON_SIZE = 'middle' as const;

const ListView = ({
  icon,
  title,
  searchPlaceholder = 'Search...',
  searchText,
  onSearchChange,
  sortDropdown,
  createButtonLabel,
  onCreateClick,
  filterButtonLabel,
  onFilterClick,
  hasActiveFilters = false,
  dataSource,
  columns,
  rowKey,
  rowSelection,
  pagination,
  onTableChange,
  onRowClick,
  isLoading = false,
  emptyMessage = 'No items found.',
  detailPanel,
  filterDrawer,
  className = ''
}: Readonly<ListViewProps>) => {
  const navContentRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const calculateHeight = () => {
      const navContent = navContentRef.current;
      if (navContent) {
        const { top } = navContent.getBoundingClientRect();
        const calculatedHeight = window.innerHeight - top - PAGINATION_HEIGHT;

        navContent.style.setProperty(
          '--table-container-height',
          `${calculatedHeight}px`
        );
      }
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  return (
    <div className={`application-list-view ${className}`}>
      <div className="application-nav-panel">
        <div className="nav-header">
          <div className="header-title-section">
            <div className="flex items-center">
              {icon}
              <h1 className="header-title">{title}</h1>
            </div>
          </div>

          <div className="header-search-section">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-1">
                <Input
                  placeholder={searchPlaceholder}
                  prefix={
                    <SearchIcon className={`${ICON_CLASS} text-gray-400`} />
                  }
                  value={searchText}
                  onChange={e => onSearchChange(e.target.value)}
                  allowClear
                  size="middle"
                />
              </div>
              <BlueBadge visible={hasActiveFilters}>
                <Button
                  type={ButtonType.outline}
                  size={BUTTON_SIZE}
                  icon={<FilterIcon width="12" height="12" />}
                  onClick={onFilterClick}
                  aria-label={filterButtonLabel}
                  title={filterButtonLabel}
                />
              </BlueBadge>
            </div>
          </div>

          <div className="header-actions-section">
            <div className="flex items-center">
              <div>
                <Button
                  type={ButtonType.primary}
                  size={BUTTON_SIZE}
                  icon={
                    <PlusIcon
                      width="16"
                      height="16"
                      style={{ flexShrink: 0 }}
                    />
                  }
                  onClick={onCreateClick}
                  style={{ width: '120px', padding: '0 11px' }}
                >
                  {createButtonLabel}
                </Button>
              </div>
              <div className="flex-1 flex-grow flex justify-end">
                {sortDropdown}
              </div>
            </div>
          </div>
        </div>

        <div className="nav-content" ref={navContentRef}>
          {isLoading && (
            <LoadingSpinner message="Loading records..." className="pt-8" />
          )}
          {!isLoading && dataSource.length > 0 && (
            <Table
              rowKey={rowKey}
              dataSource={dataSource}
              columns={columns}
              pagination={pagination}
              onChange={onTableChange}
              rowSelection={rowSelection}
              size="small"
              showHeader={false}
              scroll={{ y: 'var(--table-container-height)' }}
              onRow={
                onRowClick
                  ? (record: any) => ({
                      tabIndex: 0,
                      onClick: () => onRowClick(record),
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(record);
                        }
                      }
                    })
                  : undefined
              }
            />
          )}
          {!isLoading && dataSource.length === 0 && (
            <div
              data-testid="no-items"
              className="flex flex-col items-center py-5 bg-white"
              style={{
                minHeight: `calc(${navContentRef.current?.style.getPropertyValue(
                  '--table-container-height'
                ) || '0px'} + ${PAGINATION_HEIGHT}px)`
              }}
            >
              <p>{emptyMessage}</p>
            </div>
          )}
        </div>
      </div>
      {detailPanel}
      {filterDrawer}
    </div>
  );
};

export default ListView;
